import { ResolutionType } from "application/contexts/screen/ScreenStore";
import Toolbar from "areas/_catalog/components/toolbar/Toolbar";
import { Keyboard } from "components/keyboard/Keyboard";
import { numberMoneyMask } from "corss-cutting/masks/money";
import { IOrderProviderRef } from "modules/order/presentation/OrderProvider";
import React, { FC, useCallback, useMemo, useRef } from "react";
import { hideCnpj, hideCpf, validateCNPJ, validateCPF } from "utils/utils";
import totemGif from "../../../assets/images/totem.gif";
import styles from "./OfflineRecharge.module.scss";
import { OfflineRechargeSteps } from "./OfflineRecharge.types";
import { Reading } from "./components/reading/Reading";
import { SelectCardType } from "./components/selectCardType/SelectCardType";
import useOfflineRecharge from "./useOfflineRecharge";
import { IFullCardData } from "domains/cashless/aggregates/ICardCashless";
import { Trans } from "react-i18next";
import CustomerIdentification from "modules/order/presentation/customerIdentification/CustomerIdentification";
import { ICustomerIdentificationHandler } from "modules/order/presentation/customerIdentification/CustomerIdentification";
import { Button } from "@material-ui/core";

interface IOfflineRechargeProps {
  orderProvider: React.RefObject<IOrderProviderRef>;
}

export const OfflineRecharge: FC<IOfflineRechargeProps> = ({
  orderProvider,
}) => {
  const {
    onPressBack,
    onPressKeyboard,
    paymentCashlessOffline,
    step,
    offlineCardData,
    rechargeValue,
    kiosk,
    activeLoad,
    onClickBack,
    resolution,
    addPreviousValue,
    t,
    customerRef,
    isClosed,
    activationDisplayValue,
    showBackButtonRecord,
    showBackButtonPendingRecord,
    onCancelRecord,
  } = useOfflineRecharge(orderProvider);


  const renderDocument = useCallback((offlineCardData: IFullCardData) => {
    const document = offlineCardData?.Document;
    if (validateCPF(document ?? "")) {
      return `CPF: ${hideCpf(document ?? "")}`;
    }

    if (validateCNPJ(document ?? "")) {
      return `CNPJ: ${hideCnpj(document ?? "")}`;
    }

    if (offlineCardData?.CountryCode) {
      return `${t('offline_recharge.pay_recharge.cpf_label')}: ${document} (${offlineCardData.CountryCode})`;
    }

    return `${t('offline_recharge.pay_recharge.cpf_label')}: ${document}`;
  }, [t]);

  const renderStep = useCallback(() => {
    switch (step) {
      case OfflineRechargeSteps.READ:
        return (
          <>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
              className={styles.selected}
            >
              1
            </div>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
            >
              2
            </div>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
            >
              3
            </div>
          </>
        );
      case OfflineRechargeSteps.PAY_RECHARGE:
        return (
          <>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
            >
              1
            </div>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
              className={styles.selected}
            >
              2
            </div>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
            >
              3
            </div>
          </>
        );
      case OfflineRechargeSteps.RECORD:
        return (
          <>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
            >
              1
            </div>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
            >
              2
            </div>
            <div
              style={{
                minWidth: resolution === ResolutionType.FULL_HD ? 120 : 100,
                height: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
              className={styles.selected}
            >
              3
            </div>
          </>
        );
    }
  }, [resolution, step]);

  return (
    <div id={styles.OfflineRecharge}>
      <Reading active={activeLoad} />

      <Toolbar
        hideWelcome
        title={kiosk?.localName ?? ""}
        handleGoBack={onClickBack}
        hideCloseButton={step === OfflineRechargeSteps.RECORD || step === OfflineRechargeSteps.PENDING_RECORD}
      />

      <div
        className={styles.stepContainer}
        style={{
          margin: resolution === ResolutionType.FULL_HD ? "36px 0" : "12px 0",
          fontSize: resolution === ResolutionType.FULL_HD ? 40 : 30,
        }}
      >
        {renderStep()}
      </div>

      {step === OfflineRechargeSteps.READ && (
        <>
          <div>
            <div
              className={styles.readCardMessage}
              style={{
                margin: resolution === ResolutionType.FULL_HD ? 150 : 75,
              }}
            >
              <p
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 70 : 50,
                }}
              >
                <Trans i18nKey="offline_recharge.read.line1">{' '}<b>Para começar,</b> aproxime</Trans>
              </p>
              <p
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 70 : 50,
                }}
              >
                <Trans i18nKey="offline_recharge.read.line2">seu <b>cartão/pulseira</b></Trans>
              </p>
            </div>
            <div>
              <img
                className={styles.totemGif}
                src={totemGif}
                alt="totem"
                style={{
                  width: resolution === ResolutionType.FULL_HD ? 900 : 500,
                  height: resolution === ResolutionType.FULL_HD ? 500 : 300,
                }}
              />
            </div>
          </div>
          <div
            style={{
              height: resolution === ResolutionType.FULL_HD ? 600 : 300,
            }}
          />
        </>
      )}

      {step === OfflineRechargeSteps.PAY_RECHARGE && offlineCardData && (
        <>
          {offlineCardData?.Document && (
            <p
              className={styles.cpf}
              style={{
                margin:
                  resolution === ResolutionType.FULL_HD ? "36px 0" : "12px 0",
              }}
            >
              {renderDocument(offlineCardData)}
            </p>
          )}
          <div className={styles.balance}>
            <p
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 30 : 26,
              }}
            >
              {t('offline_recharge.pay_recharge.balance')}
            </p>
            <p
              style={{
                fontSize: resolution === ResolutionType.FULL_HD ? 80 : 60,
              }}
            >
              {numberMoneyMask(offlineCardData.Balance / 100)}
            </p>
          </div>

          <div
            className={styles.rechargeValue}
            style={{
              margin: resolution === ResolutionType.FULL_HD ? 48 : 0,
            }}
          >
            {isClosed && activationDisplayValue > 0 && (
              <p
                className={styles.activationCost}
                style={{
                  marginBottom: resolution === ResolutionType.FULL_HD ? 16 : 12,
                  fontSize: resolution === ResolutionType.FULL_HD ? 28 : 22,
                }}
              >
                {t('offline_recharge.pay_recharge.activation_cost')}: {numberMoneyMask(activationDisplayValue)}
              </p>
            )}
            <div
              className={styles.previousValueContainer}
              style={{
                padding:
                  resolution === ResolutionType.FULL_HD ? "0 24px" : "0 12px",
                marginBottom: resolution === ResolutionType.FULL_HD ? 50 : 30,
              }}
            >
              <div
                style={{
                  padding:
                    resolution === ResolutionType.FULL_HD
                      ? "24px 36px"
                      : "20px",
                  fontSize: resolution === ResolutionType.FULL_HD ? 20 : 16,
                }}
                onClick={() => addPreviousValue(10)}
              >{`+ ${numberMoneyMask(10)}`}</div>
              <div
                style={{
                  padding:
                    resolution === ResolutionType.FULL_HD
                      ? "24px 36px"
                      : "20px",
                  fontSize: resolution === ResolutionType.FULL_HD ? 20 : 16,
                }}
                onClick={() => addPreviousValue(30)}
              >{`+ ${numberMoneyMask(30)}`}</div>
              <div
                style={{
                  padding:
                    resolution === ResolutionType.FULL_HD
                      ? "24px 36px"
                      : "20px",
                  fontSize: resolution === ResolutionType.FULL_HD ? 20 : 16,
                }}
                onClick={() => addPreviousValue(50)}
              >{`+ ${numberMoneyMask(50)}`}</div>
              <div
                style={{
                  padding:
                    resolution === ResolutionType.FULL_HD
                      ? "24px 36px"
                      : "20px",
                  fontSize: resolution === ResolutionType.FULL_HD ? 20 : 16,
                }}
                onClick={() => addPreviousValue(100)}
              >{`+ ${numberMoneyMask(100)}`}</div>
            </div>
            <div
              className={styles.rechargeValueContainer}
              style={{
                margin: resolution === ResolutionType.FULL_HD ? 24 : 16,
                marginBottom: resolution === ResolutionType.FULL_HD ? 80 : 40,
              }}
            >
              <div
                className={
                  rechargeValue > 0 ? styles.value : styles.placeholder
                }
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 45 : 30,
                }}
              >
                {rechargeValue > 0
                  ? numberMoneyMask(rechargeValue / 100)
                  : t('offline_recharge.pay_recharge.placeholder')}
              </div>
            </div>
            <Keyboard
              type="clear"
              hideClear
              onPressBack={onPressBack}
              onKeyPress={onPressKeyboard}
            />
          </div>

          <div className={styles.paymentForm}>
            <div>
              {kiosk && (
                <SelectCardType
                  kiosk={kiosk}
                  onSelect={paymentCashlessOffline}
                  rechargePayment
                />
              )}
            </div>
          </div>
        </>
      )}

      {step === OfflineRechargeSteps.RECORD && (
        <>
          <div className={styles.recordContainer}>
            <div className={styles.recordCardMessage}>
              <p
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 70 : 50,
                }}
              >
                <b>{t('offline_recharge.record.title')}</b>
              </p>
            </div>
            <div>
              <img
                className={styles.totemGif}
                src={totemGif}
                alt="totem"
                style={{
                  width: resolution === ResolutionType.FULL_HD ? 900 : 500,
                  height: resolution === ResolutionType.FULL_HD ? 500 : 300,
                }}
              />
            </div>
            <div className={styles.recordCardFooterMessage}>
              <p
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 40 : 30,
                }}
              >
                <Trans i18nKey="offline_recharge.record.line1">{' '}<b>Aproxime</b> seu cartão/pulseira</Trans>
              </p>
              <p
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 40 : 30,
                }}
              >
                <Trans i18nKey="offline_recharge.record.line2">para <b>gravar o saldo</b></Trans>
              </p>
            </div>
            {showBackButtonRecord && (
              <div className={styles.recordBackButtonWrapper}>
                <Button
                  className={styles.recordBackButton}
                  onClick={onCancelRecord}
                  size="large"
                  style={{
                    fontSize: resolution === ResolutionType.FULL_HD ? 32 : 24,
                    padding: resolution === ResolutionType.FULL_HD ? "16px 32px" : "12px 24px",
                  }}
                >
                  {t("offline_recharge.record.cancel")}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {step === OfflineRechargeSteps.PENDING_RECORD && (
        <>
          <div className={styles.recordContainer}>
            <div className={styles.recordCardMessage}>
              <p
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 70 : 50,
                }}
              >
                <b>{t('offline_recharge.pending_record.title')}</b>
              </p>
            </div>
            <div>
              <img
                className={styles.totemGif}
                src={totemGif}
                alt="totem"
                style={{
                  width: resolution === ResolutionType.FULL_HD ? 900 : 500,
                  height: resolution === ResolutionType.FULL_HD ? 500 : 300,
                }}
              />
            </div>
            <div className={styles.recordCardFooterMessage}>
              <p
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 40 : 30,
                }}
              >
                <Trans i18nKey="offline_recharge.pending_record.line1">
                  Aguarde e <b>não afaste</b> o cartão ou pulseira
                </Trans>
              </p>
            </div>
            {showBackButtonPendingRecord && (
              <div className={styles.recordBackButtonWrapper}>
                <Button
                  className={styles.recordBackButton}
                  onClick={onCancelRecord}
                  size="large"
                  style={{
                    fontSize: resolution === ResolutionType.FULL_HD ? 32 : 24,
                    padding: resolution === ResolutionType.FULL_HD ? "16px 32px" : "12px 24px",
                  }}
                >
                  {t("offline_recharge.pending_record.cancel")}
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      <CustomerIdentification ref={customerRef} />

    </div>
  );
};
